import { useEffect } from 'react'
import { sileo } from 'sileo'
import {
  useMutationCreateInventoryItem,
  useMutationCreateInventoryManualMovement,
  useMutationCreateInventoryRecipe,
  useMutationCreateInventoryTransfer,
  useMutationDeleteInventoryItem,
  useMutationUpdateInventoryItem,
  useMutationCreateInventoryWarehouse,
  useMutationDeleteInventoryWarehouse,
  useMutationUpdateInventoryWarehouse,
  useMutationUploadInventoryInitialStock,
  useMutationUploadInventoryItems
} from '../../../../../../queries/inventoryQueries'
import {
  DEFAULT_WAREHOUSE_CODE,
  DEFAULT_WAREHOUSE_NAME,
  MAX_WAREHOUSES
} from '../constants/inventoryWarehouse'
import {
  createEditableItemFormState,
  createEditableWarehouseFormState,
  createItemFormState,
  createManualFormState,
  createRecipeComponentsState,
  createTransferFormState,
  createWarehouseFormState
} from './useInventoryPageState'
import {
  getCanonicalBaseUnit,
  getDefaultInputUnit,
  isCanonicalBaseUnit
} from '../utils/inventoryUnits'
import {
  getErrorMessage,
  resolveItemId,
  resolveItemType,
  resolveWarehouseId,
  toBoolean
} from '../utils/inventoryResolvers'

const useInventoryActions = ({ businessId, data, navigation, state }) => {
  const createManualMovementMutation = useMutationCreateInventoryManualMovement()
  const createTransferMutation = useMutationCreateInventoryTransfer()
  const createRecipeMutation = useMutationCreateInventoryRecipe()
  const createWarehouseMutation = useMutationCreateInventoryWarehouse()
  const deleteWarehouseMutation = useMutationDeleteInventoryWarehouse()
  const updateWarehouseMutation = useMutationUpdateInventoryWarehouse()
  const createItemMutation = useMutationCreateInventoryItem()
  const deleteItemMutation = useMutationDeleteInventoryItem()
  const updateItemMutation = useMutationUpdateInventoryItem()
  const uploadItemsMutation = useMutationUploadInventoryItems()
  const uploadInitialStockMutation = useMutationUploadInventoryInitialStock()

  const buildItemPayload = formState => {
    const canonicalUnit = getCanonicalBaseUnit(formState.unit)
    const payload = {
      sku: formState.sku.trim(),
      name: formState.name.trim(),
      unit: canonicalUnit,
      controlsInventory: formState.controlsInventory,
      usesRecipe: formState.usesRecipe,
      active: formState.active
    }

    const itemType = formState.itemType.trim()
    if (itemType) {
      payload.itemType = itemType
    }

    const normalizedPrice =
      formState.price === '' ? undefined : Number(String(formState.price).replace(',', '.'))

    if (normalizedPrice !== undefined && Number.isFinite(normalizedPrice)) {
      payload.price = normalizedPrice
    }

    return payload
  }

  const mapRowToItemEditForm = row => ({
    itemId: String(resolveItemId(row) || ''),
    sku: String(row?.sku ?? ''),
    name: String(row?.name ?? row?.nombre ?? ''),
    unit: getCanonicalBaseUnit(row?.unit ?? row?.unidad ?? 'UN'),
    itemType: String(resolveItemType(row) || 'PRODUCT').toUpperCase(),
    controlsInventory: toBoolean(
      row?.controls_inventory ?? row?.controlsInventory ?? row?.controla_inventario ?? false
    ),
    usesRecipe: toBoolean(row?.uses_recipe ?? row?.usesRecipe ?? row?.usa_receta ?? false),
    price:
      row?.price === null || row?.price === undefined
        ? row?.precio === null || row?.precio === undefined
          ? ''
          : String(row?.precio)
        : String(row?.price),
    active: toBoolean(row?.active ?? row?.is_active ?? row?.isActive ?? true)
  })

  const getStockItemOptionById = itemId =>
    data.stockOperationItemOptions.find(option => String(option.id) === String(itemId)) || null

  const getAnyItemOptionById = itemId =>
    data.itemOptions.find(option => String(option.id) === String(itemId)) || null

  const handleRefreshAll = async () => {
    await Promise.allSettled([
      data.warehousesQuery.refetch(),
      data.itemsQuery.refetch(),
      data.stockQuery.refetch(),
      data.movementsQuery.refetch(),
      data.recipeByItemQuery.refetch(),
      data.recipeComponentsQuery.refetch()
    ])

    sileo.success({
      title: 'Inventario actualizado',
      description: 'Se refrescaron bodegas, items, stock y movimientos.'
    })
  }

  useEffect(() => {
    if (!businessId || data.warehousesQuery.isLoading || data.warehousesQuery.isFetching) return
    if (data.warehouses.length > 0 || !data.hasWarehouseCapacity) return
    if (state.initialWarehouseBootstrapRef.current[businessId]) return

    state.initialWarehouseBootstrapRef.current[businessId] = true

    createWarehouseMutation.mutate(
      {
        businessId,
        payload: {
          code: DEFAULT_WAREHOUSE_CODE,
          name: DEFAULT_WAREHOUSE_NAME,
          isDefault: true,
          isActive: true
        }
      },
      {
        onSuccess: () => {
          sileo.success({
            title: 'Bodega inicial creada',
            description: 'Se creó la bodega principal para empezar a operar inventario.'
          })
        },
        onError: error => {
          sileo.error({
            title: 'No se pudo crear la bodega inicial',
            description: getErrorMessage(error)
          })
        }
      }
    )
  }, [
    businessId,
    createWarehouseMutation,
    data.hasWarehouseCapacity,
    data.warehouses,
    data.warehousesQuery.isFetching,
    data.warehousesQuery.isLoading,
    state.initialWarehouseBootstrapRef
  ])

  const handleRequestDeleteWarehouse = row => {
    const warehouseId = resolveWarehouseId(row)
    if (!warehouseId) return

    state.setWarehouseToDelete({
      id: warehouseId,
      code: row?.code ?? row?.codigo ?? 'SIN-COD',
      name: row?.name ?? row?.nombre ?? 'Sin nombre'
    })
  }

  const handleCloseDeleteWarehouseModal = () => {
    if (deleteWarehouseMutation.isPending) return
    state.setWarehouseToDelete(null)
  }

  const handleRequestDeleteItem = row => {
    const itemId = resolveItemId(row)
    const isActive = toBoolean(row?.active ?? row?.is_active ?? row?.isActive ?? true)

    if (!itemId) return
    if (!isActive) return

    state.setItemToDelete({
      id: itemId,
      sku: row?.sku ?? 'SIN-SKU',
      name: row?.name ?? row?.nombre ?? 'Sin nombre'
    })
  }

  const handleCloseDeleteItemModal = () => {
    if (deleteItemMutation.isPending) return
    state.setItemToDelete(null)
  }

  const handleConfirmDeleteWarehouse = async () => {
    if (!state.warehouseToDelete?.id) return

    try {
      await deleteWarehouseMutation.mutateAsync({
        businessId,
        warehouseId: Number(state.warehouseToDelete.id)
      })

      state.setManualForm(prev =>
        prev.warehouseId === String(state.warehouseToDelete.id) ? { ...prev, warehouseId: '' } : prev
      )
      state.setTransferForm(prev => ({
        ...prev,
        fromWarehouseId:
          prev.fromWarehouseId === String(state.warehouseToDelete.id) ? '' : prev.fromWarehouseId,
        toWarehouseId:
          prev.toWarehouseId === String(state.warehouseToDelete.id) ? '' : prev.toWarehouseId
      }))
      state.setInitialStockWarehouseId(prev =>
        prev === String(state.warehouseToDelete.id) ? '' : prev
      )
      state.setWarehouseToDelete(null)

      sileo.success({
        title: 'Bodega desactivada',
        description: `La bodega ${state.warehouseToDelete.code} dejó de estar disponible en el sistema.`
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo eliminar la bodega',
        description: getErrorMessage(error)
      })
    }
  }

  const handleConfirmDeleteItem = async () => {
    if (!state.itemToDelete?.id) return

    const itemToDelete = state.itemToDelete

    try {
      const response = await deleteItemMutation.mutateAsync({
        businessId,
        itemId: Number(itemToDelete.id)
      })

      if (state.itemEditForm.itemId === String(itemToDelete.id)) {
        state.setIsEditItemModalOpen(false)
        state.setItemEditForm(createEditableItemFormState())
      }

      state.setItemToDelete(null)

      sileo.success({
        title: 'Item eliminado',
        description: response?.data?.message || `El item ${itemToDelete.sku} fue desactivado correctamente.`
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo eliminar el item',
        description: getErrorMessage(error)
      })
    }
  }

  const handleWarehouseSubmit = async event => {
    event.preventDefault()

    const code = data.nextWarehouseCode
    const name = state.warehouseForm.name.trim()

    if (!name) {
      sileo.warning({
        title: 'Datos incompletos',
        description: 'Debes informar el nombre de la bodega.'
      })
      return
    }

    if (!code) {
      sileo.warning({
        title: 'Límite alcanzado',
        description: `El módulo admite hasta ${MAX_WAREHOUSES} bodegas.`
      })
      return
    }

    const payload = {
      code,
      name,
      isDefault: state.warehouseForm.isDefault,
      isActive: state.warehouseForm.isActive
    }

    try {
      await createWarehouseMutation.mutateAsync({ businessId, payload })
      state.setWarehouseForm(createWarehouseFormState())
      state.setIsCreateWarehouseModalOpen(false)
      sileo.success({
        title: 'Bodega creada',
        description: `Se creó la bodega ${name}.`
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo crear la bodega',
        description: getErrorMessage(error)
      })
    }
  }

  const handleOpenCreateWarehouseModal = () => {
    state.setIsCreateWarehouseModalOpen(true)
  }

  const handleCloseCreateWarehouseModal = () => {
    if (createWarehouseMutation.isPending) return
    state.setIsCreateWarehouseModalOpen(false)
    state.setWarehouseForm(createWarehouseFormState())
  }

  const mapRowToWarehouseEditForm = row => ({
    warehouseId: String(resolveWarehouseId(row) || ''),
    code: String(row?.code ?? row?.codigo ?? ''),
    name: String(row?.name ?? row?.nombre ?? ''),
    isDefault: toBoolean(row?.is_default ?? row?.isDefault ?? row?.default)
  })

  const handleOpenEditWarehouseModal = row => {
    const warehouseId = resolveWarehouseId(row)

    if (!warehouseId) {
      sileo.warning({
        title: 'Bodega inválida',
        description: 'No se pudo identificar la bodega que intentas editar.'
      })
      return
    }

    state.setWarehouseEditForm(mapRowToWarehouseEditForm(row))
    state.setIsEditWarehouseModalOpen(true)
  }

  const handleCloseEditWarehouseModal = () => {
    if (updateWarehouseMutation.isPending) return
    state.setIsEditWarehouseModalOpen(false)
    state.setWarehouseEditForm(createEditableWarehouseFormState())
  }

  const handleEditWarehouseSubmit = async event => {
    event.preventDefault()

    const warehouseId = Number(state.warehouseEditForm.warehouseId || 0)
    const name = state.warehouseEditForm.name.trim()

    if (!warehouseId) {
      sileo.warning({
        title: 'Bodega inválida',
        description: 'No se pudo identificar la bodega que intentas actualizar.'
      })
      return
    }

    if (!name) {
      sileo.warning({
        title: 'Datos incompletos',
        description: 'Debes informar el nombre de la bodega.'
      })
      return
    }

    const payload = {
      name,
      isDefault: state.warehouseEditForm.isDefault
    }

    try {
      await updateWarehouseMutation.mutateAsync({ businessId, warehouseId, payload })
      handleCloseEditWarehouseModal()
      sileo.success({
        title: 'Bodega actualizada',
        description: `Se actualizó la bodega ${name}.`
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo actualizar la bodega',
        description: getErrorMessage(error)
      })
    }
  }

  const handleItemSubmit = async event => {
    event.preventDefault()

    const sku = state.itemForm.sku.trim()
    const name = state.itemForm.name.trim()
    const unit = getCanonicalBaseUnit(state.itemForm.unit)

    if (!sku || !name || !unit) {
      sileo.warning({
        title: 'Datos incompletos',
        description: 'SKU, nombre y unidad base son obligatorios.'
      })
      return
    }

    if (!isCanonicalBaseUnit(unit)) {
      sileo.warning({
        title: 'Unidad base inválida',
        description: 'La unidad base del item debe ser UN, KG o LT.'
      })
      return
    }

    const payload = buildItemPayload(state.itemForm)

    try {
      await createItemMutation.mutateAsync({ businessId, payload })
      state.setItemForm(createItemFormState())
      state.setIsCreateItemModalOpen(false)
      sileo.success({
        title: 'Item creado',
        description: `Se creó el item ${sku}.`
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo crear el item',
        description: getErrorMessage(error)
      })
    }
  }

  const handleOpenCreateItemModal = () => {
    state.setIsCreateItemModalOpen(true)
  }

  const handleCloseCreateItemModal = () => {
    if (createItemMutation.isPending) return
    state.setIsCreateItemModalOpen(false)
    state.setItemForm(createItemFormState())
  }

  const handleOpenEditItemModal = row => {
    const itemId = resolveItemId(row)

    if (!itemId) {
      sileo.warning({
        title: 'Item inválido',
        description: 'No se pudo identificar el item que intentas editar.'
      })
      return
    }

    state.setItemEditForm(mapRowToItemEditForm(row))
    state.setIsEditItemModalOpen(true)
  }

  const handleCloseEditItemModal = () => {
    if (updateItemMutation.isPending) return
    state.setIsEditItemModalOpen(false)
    state.setItemEditForm(createEditableItemFormState())
  }

  const handleEditItemSubmit = async event => {
    event.preventDefault()

    const itemId = Number(state.itemEditForm.itemId || 0)
    const sku = state.itemEditForm.sku.trim()
    const name = state.itemEditForm.name.trim()
    const unit = getCanonicalBaseUnit(state.itemEditForm.unit)

    if (!itemId) {
      sileo.warning({
        title: 'Item inválido',
        description: 'No se pudo identificar el item que intentas actualizar.'
      })
      return
    }

    if (!sku || !name || !unit) {
      sileo.warning({
        title: 'Datos incompletos',
        description: 'SKU, nombre y unidad base son obligatorios.'
      })
      return
    }

    if (!isCanonicalBaseUnit(unit)) {
      sileo.warning({
        title: 'Unidad base inválida',
        description: 'La unidad base del item debe ser UN, KG o LT.'
      })
      return
    }

    const payload = buildItemPayload(state.itemEditForm)

    try {
      await updateItemMutation.mutateAsync({ businessId, itemId, payload })
      handleCloseEditItemModal()
      sileo.success({
        title: 'Item actualizado',
        description: `Se actualizó el item ${sku}.`
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo actualizar el item',
        description: getErrorMessage(error)
      })
    }
  }

  const handleManualMovementSubmit = async event => {
    event.preventDefault()

    if (
      !state.manualForm.itemId ||
      !state.manualForm.warehouseId ||
      !state.manualForm.movementType ||
      !state.manualForm.quantity
    ) {
      sileo.warning({
        title: 'Datos incompletos',
        description: 'Debes seleccionar item, bodega, tipo y cantidad.'
      })
      return
    }

    const quantity = Number(state.manualForm.quantity)
    if (!Number.isFinite(quantity) || quantity <= 0) {
      sileo.warning({
        title: 'Cantidad inválida',
        description: 'La cantidad debe ser mayor que cero.'
      })
      return
    }

    const selectedItemOption = getStockItemOptionById(state.manualForm.itemId)
    const qtyUnit = state.manualForm.qtyUnit || getDefaultInputUnit(selectedItemOption?.unit || '')

    const payload = {
      itemId: Number(state.manualForm.itemId),
      warehouseId: Number(state.manualForm.warehouseId),
      movementType: state.manualForm.movementType,
      quantity,
      qtyUnit: qtyUnit || undefined,
      note: state.manualForm.note.trim() || undefined
    }

    try {
      await createManualMovementMutation.mutateAsync({ businessId, payload })
      state.setManualForm(createManualFormState())
      sileo.success({
        title: 'Movimiento registrado',
        description: 'El movimiento manual se creó correctamente.'
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo crear el movimiento',
        description: getErrorMessage(error)
      })
    }
  }

  const handleTransferSubmit = async event => {
    event.preventDefault()

    if (
      !state.transferForm.itemId ||
      !state.transferForm.fromWarehouseId ||
      !state.transferForm.toWarehouseId ||
      !state.transferForm.quantity
    ) {
      sileo.warning({
        title: 'Datos incompletos',
        description: 'Debes seleccionar item, bodegas origen/destino y cantidad.'
      })
      return
    }

    if (state.transferForm.fromWarehouseId === state.transferForm.toWarehouseId) {
      sileo.warning({
        title: 'Bodegas inválidas',
        description: 'La bodega origen y destino no pueden ser iguales.'
      })
      return
    }

    const quantity = Number(state.transferForm.quantity)
    if (!Number.isFinite(quantity) || quantity <= 0) {
      sileo.warning({
        title: 'Cantidad inválida',
        description: 'La cantidad debe ser mayor que cero.'
      })
      return
    }

    const selectedItemOption = getStockItemOptionById(state.transferForm.itemId)
    const qtyUnit = state.transferForm.qtyUnit || getDefaultInputUnit(selectedItemOption?.unit || '')

    const payload = {
      itemId: Number(state.transferForm.itemId),
      fromWarehouseId: Number(state.transferForm.fromWarehouseId),
      toWarehouseId: Number(state.transferForm.toWarehouseId),
      quantity,
      qtyUnit: qtyUnit || undefined,
      note: state.transferForm.note.trim() || undefined
    }

    try {
      await createTransferMutation.mutateAsync({ businessId, payload })
      state.setTransferForm(createTransferFormState())
      sileo.success({
        title: 'Transferencia registrada',
        description: 'La transferencia se procesó correctamente.'
      })
    } catch (error) {
      sileo.error({
        title: 'No se pudo crear la transferencia',
        description: getErrorMessage(error)
      })
    }
  }

  const handleAddRecipeComponent = () => {
    state.setRecipeComponents(prev => [
      ...prev,
      {
        componentItemId: '',
        qtyRequired: '',
        displayQuantity: '',
        displayUnit: '',
        wastePct: '',
        sortOrder: String(prev.length + 1)
      }
    ])
  }

  const handleRemoveRecipeComponent = index => {
    state.setRecipeComponents(prev => prev.filter((_, idx) => idx !== index))
  }

  const handleRecipeComponentChange = (index, field, value) => {
    state.setRecipeComponents(prev =>
      prev.map((component, idx) => (idx === index ? { ...component, [field]: value } : component))
    )
  }

  const handleCreateRecipeSubmit = async event => {
    event.preventDefault()

    if (!state.selectedBomItemId) {
      sileo.warning({
        title: 'Falta el producto',
        description: 'Primero selecciona el producto que quieres construir con esta receta.'
      })
      return
    }

    if (state.recipeComponents.length === 0) {
      sileo.warning({
        title: 'Faltan ingredientes',
        description: 'Agrega al menos un ingrediente para guardar la receta.'
      })
      return
    }

    const selectedComponentIds = state.recipeComponents.map(component => Number(component.componentItemId || 0))
    if (selectedComponentIds.some(componentId => componentId <= 0)) {
      sileo.warning({
        title: 'Ingrediente incompleto',
        description: 'Todos los ingredientes deben tener un item seleccionado.'
      })
      return
    }

    const uniqueComponentIds = new Set(selectedComponentIds)
    if (uniqueComponentIds.size !== selectedComponentIds.length) {
      sileo.warning({
        title: 'Ingredientes duplicados',
        description: 'No repitas el mismo ingrediente dentro de una misma receta.'
      })
      return
    }

    if (selectedComponentIds.some(componentId => componentId === Number(state.selectedBomItemId))) {
      sileo.warning({
        title: 'Ingrediente inválido',
        description: 'El producto final no puede ser ingrediente de su propia receta.'
      })
      return
    }

    const invalidQty = state.recipeComponents.some(component => {
      const qtyRequired = Number(component.qtyRequired)
      return !Number.isFinite(qtyRequired) || qtyRequired <= 0
    })

    if (invalidQty) {
      sileo.warning({
        title: 'Cantidad inválida',
        description: 'Cada ingrediente debe tener una cantidad mayor a cero.'
      })
      return
    }

    const yieldQty = Number(state.recipeForm.yieldQty)
    if (!Number.isFinite(yieldQty) || yieldQty <= 0) {
      sileo.warning({
        title: 'Rendimiento inválido',
        description: 'Indica cuántas unidades finales produce esta receta.'
      })
      return
    }

    const payload = {
      itemId: Number(state.selectedBomItemId),
      version: state.recipeForm.version.trim() || '1',
      yieldQty,
      active: state.recipeForm.active,
      components: state.recipeComponents.map(component => {
        const componentItemOption = getAnyItemOptionById(component.componentItemId)
        const qtyUnit = component.displayUnit || getDefaultInputUnit(componentItemOption?.unit || '')

        return {
          componentItemId: Number(component.componentItemId),
          qtyRequired: Number(component.qtyRequired),
          qtyUnit: qtyUnit || undefined,
          wastePct:
            component.wastePct === '' || component.wastePct === null
              ? undefined
              : Number(component.wastePct),
          sortOrder:
            component.sortOrder === '' || component.sortOrder === null
              ? undefined
              : Number(component.sortOrder)
        }
      })
    }

    try {
      await createRecipeMutation.mutateAsync({
        businessId,
        itemId: Number(state.selectedBomItemId),
        payload
      })
      sileo.success({
        title: 'Receta guardada',
        description: 'La receta se guardó correctamente para este producto.'
      })
      state.setRecipeComponents(createRecipeComponentsState())
    } catch (error) {
      sileo.error({
        title: 'No se pudo guardar la receta',
        description: getErrorMessage(error)
      })
    }
  }

  const appendUploadHistory = (mode, fileName, result) => {
    const nextEntry = {
      id: `${mode}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      mode,
      fileName: fileName || 'Sin archivo',
      createdAt: new Date().toISOString(),
      batchCode: result?.batchCode || '',
      inserted: result?.inserted ?? null,
      updated: result?.updated ?? null,
      processed: result?.processed ?? null,
      movementsCreated: result?.movementsCreated ?? null,
      errors: result?.errors ?? 0
    }

    state.setUploadHistory(prev => [nextEntry, ...prev])
  }

  const handleUploadItems = async () => {
    if (!state.itemsFile) {
      sileo.warning({
        title: 'Archivo requerido',
        description: 'Selecciona un archivo Excel para cargar productos.'
      })
      return
    }

    try {
      const response = await uploadItemsMutation.mutateAsync({ businessId, file: state.itemsFile })
      const result = response?.data || {}
      const fileName = state.itemsFile?.name || ''

      state.setItemsUploadResult(result)
      appendUploadHistory('items', fileName, result)
      state.setItemsFile(null)
      if (state.itemsFileRef.current) state.itemsFileRef.current.value = ''

      sileo.success({
        title: 'Carga de items completada',
        description: `Insertados: ${result?.inserted ?? 0} · Actualizados: ${result?.updated ?? 0} · Errores: ${result?.errors ?? 0}`
      })
    } catch (error) {
      sileo.error({
        title: 'Error al cargar items',
        description: getErrorMessage(error)
      })
    }
  }

  const handleUploadInitialStock = async () => {
    if (!state.initialStockFile) {
      sileo.warning({
        title: 'Archivo requerido',
        description: 'Selecciona un archivo Excel para cargar stock inicial.'
      })
      return
    }

    try {
      const parsedWarehouseId = state.initialStockWarehouseId ? Number(state.initialStockWarehouseId) : undefined
      const warehouseId =
        parsedWarehouseId && Number.isFinite(parsedWarehouseId) && parsedWarehouseId > 0
          ? parsedWarehouseId
          : undefined

      const response = await uploadInitialStockMutation.mutateAsync({
        businessId,
        file: state.initialStockFile,
        warehouseId
      })
      const result = response?.data || {}
      const fileName = state.initialStockFile?.name || ''

      state.setInitialStockUploadResult(result)
      appendUploadHistory('initial-stock', fileName, result)
      state.setInitialStockFile(null)
      if (state.initialStockFileRef.current) state.initialStockFileRef.current.value = ''

      sileo.success({
        title: 'Carga inicial de stock completada',
        description: `Procesadas: ${result?.processed ?? 0} · Movimientos: ${result?.movementsCreated ?? 0} · Errores: ${result?.errors ?? 0}`
      })
    } catch (error) {
      sileo.error({
        title: 'Error en carga inicial de stock',
        description: getErrorMessage(error)
      })
    }
  }

  return {
    createManualMovementMutation,
    createTransferMutation,
    createRecipeMutation,
    createWarehouseMutation,
    deleteWarehouseMutation,
    updateWarehouseMutation,
    createItemMutation,
    deleteItemMutation,
    updateItemMutation,
    uploadItemsMutation,
    uploadInitialStockMutation,
    handleRefreshAll,
    handleRequestDeleteWarehouse,
    handleCloseDeleteWarehouseModal,
    handleConfirmDeleteWarehouse,
    handleRequestDeleteItem,
    handleCloseDeleteItemModal,
    handleConfirmDeleteItem,
    handleWarehouseSubmit,
    handleOpenCreateWarehouseModal,
    handleCloseCreateWarehouseModal,
    handleOpenEditWarehouseModal,
    handleCloseEditWarehouseModal,
    handleEditWarehouseSubmit,
    handleItemSubmit,
    handleOpenCreateItemModal,
    handleCloseCreateItemModal,
    handleOpenEditItemModal,
    handleCloseEditItemModal,
    handleEditItemSubmit,
    handleManualMovementSubmit,
    handleTransferSubmit,
    handleAddRecipeComponent,
    handleRemoveRecipeComponent,
    handleRecipeComponentChange,
    handleCreateRecipeSubmit,
    handleUploadItems,
    handleUploadInitialStock,
    navigateToSection: navigation.navigateToSection
  }
}

export default useInventoryActions
